import { useEffect, useRef } from 'react';
import L from 'leaflet';
import type { RotaPayload } from '@/features/pedidos/types';

interface Props {
  rota: RotaPayload | null;
  selecionado?: number | null;
  onSelect?: (pedidoId: number) => void;
}

const TILE = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
const TILE_ATTR = '&copy; OpenStreetMap contributors &copy; CARTO';

export function RotaMap({ rota, selecionado, onSelect }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const pathRef = useRef<SVGPathElement | null>(null);
  // marker por pedido_id para gerir tooltip sem rebuild da camada
  const markerByPedidoRef = useRef<Map<number, L.Marker>>(new Map());

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, {
      zoomControl: false,
      attributionControl: true,
      preferCanvas: true,
      // zoom fluido: passos pequenos + animação suave
      zoomSnap: 0.25,
      zoomDelta: 0.5,
      wheelDebounceTime: 30,
      wheelPxPerZoomLevel: 100,
      zoomAnimation: true,
      fadeAnimation: true,
      markerZoomAnimation: true,
      inertia: true,
      inertiaDeceleration: 2400,
      worldCopyJump: false,
    });
    mapRef.current = map;
    L.tileLayer(TILE, { attribution: TILE_ATTR, maxZoom: 19 }).addTo(map);
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    map.setView([-16.7044, -49.2469], 12);

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    markerByPedidoRef.current.clear();

    if (!rota) return;

    const pontos: [number, number][] = [
      [rota.ponto_partida.lat, rota.ponto_partida.lng],
      ...rota.rota_otimizada.map((p) => [p.latitude, p.longitude] as [number, number]),
    ];

    if (pontos.length === 0) return;

    // partida (âmbar)
    L.marker([rota.ponto_partida.lat, rota.ponto_partida.lng], {
      icon: pinIcon('amber', 'P'),
    })
      .bindTooltip(`Partida · ${rota.ponto_partida.endereco}`, { direction: 'top', className: 'leaflet-tip-floricultura' })
      .addTo(layer);

    // paradas (moss + última petal)
    rota.rota_otimizada.forEach((p, idx) => {
      const isLast = idx === rota.rota_otimizada.length - 1;
      const tone = isLast ? 'petal' : 'moss';
      // pulse stagger: cada marker delayed por idx*120ms
      const m = L.marker([p.latitude, p.longitude], {
        icon: pinIcon(tone, String(p.ordem), idx * 120),
      })
        .bindTooltip(`${p.numero_ticket} · ${p.nome_cliente}`, {
          direction: 'top',
          className: 'leaflet-tip-floricultura',
        })
        .addTo(layer);
      m.on('click', () => onSelect?.(p.pedido_id));
      markerByPedidoRef.current.set(p.pedido_id, m);
    });

    // rota — usa geojson se houver, senão polyline reta
    let pathLayer: L.Layer;
    if (rota.geojson) {
      pathLayer = L.geoJSON(rota.geojson, {
        style: {
          color: '#95b886',
          weight: 2.5,
          opacity: 1,
        },
      }).addTo(layer);
    } else {
      pathLayer = L.polyline(pontos, {
        color: '#95b886',
        weight: 2.5,
        opacity: 1,
      }).addTo(layer);
    }

    // anima stroke-dashoffset no SVG do leaflet
    requestAnimationFrame(() => {
      const inner = (pathLayer as unknown as { _path?: SVGPathElement; _layers?: Record<string, { _path?: SVGPathElement }> });
      let svg = inner._path;
      if (!svg && inner._layers) {
        const layers = Object.values(inner._layers);
        if (layers.length > 0) svg = layers[0]._path;
      }
      if (svg && svg.getTotalLength) {
        const len = svg.getTotalLength();
        svg.style.transition = 'none';
        svg.style.strokeDasharray = `${len}`;
        svg.style.strokeDashoffset = `${len}`;
        svg.getBoundingClientRect();
        svg.style.transition = 'stroke-dashoffset 1800ms cubic-bezier(0.16, 1, 0.3, 1)';
        svg.style.strokeDashoffset = '0';
        svg.style.filter = 'drop-shadow(0 0 6px rgba(149,184,134,0.4))';
        pathRef.current = svg;
      }
    });

    const bounds = L.latLngBounds(pontos);
    map.fitBounds(bounds, { padding: [80, 80], maxZoom: 14, animate: true, duration: 0.8 });
  }, [rota, onSelect]);

  // efeito leve: abre tooltip do marker selecionado sem rebuildar layer
  useEffect(() => {
    if (selecionado == null) return;
    const m = markerByPedidoRef.current.get(selecionado);
    if (m) m.openTooltip();
  }, [selecionado]);

  return <div ref={ref} className="w-full h-full" />;
}

function pinIcon(
  tone: 'amber' | 'moss' | 'petal' | 'sage',
  label: string,
  delayMs = 0,
): L.DivIcon {
  const map = {
    amber: { ring: '192,138,62', solid: '#c08a3e' },
    moss: { ring: '149,184,134', solid: '#95b886' },
    petal: { ring: '185,111,108', solid: '#b96f6c' },
    sage: { ring: '126,154,130', solid: '#7e9a82' },
  } as const;
  const { ring, solid } = map[tone];
  return L.divIcon({
    className: 'rota-marker',
    html: `
      <span class="ring" style="--ring-delay:${delayMs}ms; border-color:${solid}"></span>
      <span class="dot" style="background:${solid}; box-shadow:0 0 0 4px rgba(${ring},0.18)"></span>
      <span class="lbl">${label}</span>
    `,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

export default RotaMap;
